import React, { useState } from 'react';
import ColorChart from './ColorChart';
import { cardStyles, Slider, PlacementSliders } from './Controls';
import styles from './panelStyles';

const tabStyle = (isActive) => ({
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '5px 8px',
    background: isActive ? '#2a2a2a' : '#111',
    border: isActive ? '1px solid #ecff19' : '1px solid #333',
    borderRadius: '4px',
    cursor: 'pointer',
    boxSizing: 'border-box',
});

function TextCard({ item, config, onRemove, onUpdate }) {
    const [field, setField] = useState('fill');
    const activeColor = field === 'fill' ? item.fillColor : item.outlineColor;
    const text = item[config.valueKey];

    return (
        <div style={cardStyles.card}>
            <div style={cardStyles.cardHeader}>
                <div style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '3px',
                    background: '#111',
                    border: '1px solid #333',
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    fontFamily: config.font,
                    fontSize: config.previewSize,
                    fontWeight: config.fontWeight,
                    color: item.fillColor,
                    WebkitTextStroke: `${config.previewStroke}px ${item.outlineColor}`,
                }}>
                    {text || config.placeholder}
                </div>
                <span style={cardStyles.cardTitle}>{config.cardTitle}</span>
                <button style={cardStyles.removeBtn} onClick={() => onRemove(item.id)} title={`Remove ${config.noun}`}>
                    ✕
                </button>
            </div>

            <div style={cardStyles.divider} />

            <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                <div style={{ fontSize: '10px', color: '#666' }}>{config.inputLabel}</div>
                <input
                    type="text"
                    inputMode={config.inputMode}
                    maxLength={config.maxLength}
                    value={text}
                    placeholder={config.inputPlaceholder}
                    style={{
                        width: '100%',
                        background: '#111',
                        border: '1px solid #333',
                        borderRadius: '4px',
                        color: '#f8f9fa',
                        fontSize: config.inputSize,
                        fontWeight: config.fontWeight,
                        fontFamily: config.font,
                        textAlign: 'center',
                        padding: '6px 8px',
                        letterSpacing: '1px',
                        outline: 'none',
                        boxSizing: 'border-box',
                    }}
                    onChange={(e) => onUpdate(item.id, { [config.valueKey]: config.sanitize(e.target.value) })}
                />
            </div>

            <div style={{ display: 'flex', gap: '6px' }}>
                {[
                    { f: 'fill', label: 'Fill', color: item.fillColor },
                    { f: 'outline', label: 'Outline', color: item.outlineColor },
                ].map(({ f, label, color }) => (
                    <button key={f} style={tabStyle(field === f)} onClick={() => setField(f)}>
                        <span style={{
                            width: '14px', height: '14px', borderRadius: '3px',
                            border: '1px solid #555', flexShrink: 0, background: color,
                        }} />
                        <span style={{ fontSize: '10px', color: '#888' }}>{label}</span>
                    </button>
                ))}
            </div>

            <ColorChart
                selectedColor={activeColor}
                onSelect={(hex) => onUpdate(item.id, field === 'fill' ? { fillColor: hex } : { outlineColor: hex })}
            />

            <div style={cardStyles.divider} />

            <Slider label="Outline Width" value={item.outlineWidth} min={0} max={80} step={1}
                format={(v) => `${v}px`} onChange={(outlineWidth) => onUpdate(item.id, { outlineWidth })} />

            <div style={cardStyles.divider} />

            <PlacementSliders
                item={item}
                scaleRange={{ min: 0.1, max: 5, step: 0.05 }}
                onUpdate={(patch) => onUpdate(item.id, patch)}
            />
        </div>
    );
}

/**
 * TextPlacerPanel – add / edit / position outlined text items. Numbers and
 * names are the same thing with a different config (see NUMBER_CONFIG and
 * NAME_CONFIG in textConfigs.js).
 *
 * Props:
 *   items    – { id, <valueKey>, fillColor, outlineColor, outlineWidth, x, y, scale, rotate }[]
 *   config   – see textConfigs.js
 *   onAdd    – (item) => void
 *   onRemove – (id) => void
 *   onUpdate – (id, patch) => void
 */
export default function TextPlacerPanel({ items, config, onAdd, onRemove, onUpdate }) {
    const canAdd = items.length < config.max;

    const handleAdd = () => {
        if (!canAdd) return;
        onAdd({
            id: `${config.noun}-${Date.now()}-${Math.random().toString(36).slice(2)}`,
            [config.valueKey]: config.initialValue,
            fillColor: '#ffffff',
            outlineColor: '#000000',
            outlineWidth: config.outlineWidth,
            x: config.x,
            y: config.y,
            scale: 1,
            rotate: 0,
        });
    };

    return (
        <div style={styles.root}>
            <div>
                <div style={styles.sectionLabel}>{config.plural} ({items.length} / {config.max})</div>
                <button
                    style={cardStyles.addBtn(canAdd)}
                    disabled={!canAdd}
                    onClick={handleAdd}
                    title={canAdd ? `Add a ${config.noun} to the design` : `Maximum ${config.max} reached`}
                >
                    + Add {config.addLabel}
                </button>
            </div>

            {items.length === 0 && (
                <p style={cardStyles.hint}>
                    {config.hint}<br />
                    Choose fill and outline colors, then use the sliders to position each one.
                </p>
            )}

            {items.map((item) => (
                <TextCard key={item.id} item={item} config={config} onRemove={onRemove} onUpdate={onUpdate} />
            ))}
        </div>
    );
}
